import React from "react";
import { Link, useParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ChevronRight } from "lucide-react";
import SEO from "@/components/SEO";
import blogPosts from "@/data/blogPosts";
import { WHATSAPP_URL as WHATSAPP_LINK } from "@/constants/contact";

const BlogArticlePage = () => {
  const { slug } = useParams();
  const post = blogPosts.find((p) => p.slug === slug);
  const related = blogPosts.filter((p) => p.slug !== slug).slice(0, 2);

  if (!post) {
    return (
      <div className="bg-warm-white min-h-screen flex items-center justify-center px-4">
        <div className="text-center">
          <p className="text-sm font-medium tracking-widest text-gold uppercase mb-4">Not Found</p>
          <h1 className="font-serif text-3xl text-premium-purple mb-6">Article not found</h1>
          <Link to="/blog" className="text-gold font-medium hover:text-deep-navy transition-colors">
            ← Back to Blog
          </Link>
        </div>
      </div>
    );
  }

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    "headline": post.title,
    "description": post.metaDescription,
    "url": `https://drvickyjain.com/blog/${post.slug}`,
    "datePublished": post.dateISO || "2026-05-01",
    "dateModified": post.dateISO || "2026-05-01",
    "author": {
      "@type": "Person",
      "name": "Dr. Vicky Jain",
      "url": "https://drvickyjain.com/about",
      "jobTitle": "Plastic & Reconstructive Surgeon",
      "worksFor": {
        "@type": "MedicalBusiness",
        "name": "Dr. Vicky Jain Plastic Surgery",
        "url": "https://drvickyjain.com",
        "address": {
          "@type": "PostalAddress",
          "streetAddress": "101, Mani Mahal, Mathews Road, Opera House",
          "addressLocality": "Mumbai",
          "addressRegion": "Maharashtra",
          "postalCode": "400004",
          "addressCountry": "IN"
        }
      }
    },
    "publisher": {
      "@type": "Organization",
      "name": "Dr. Vicky Jain Plastic Surgery",
      "url": "https://drvickyjain.com",
      "logo": {
        "@type": "ImageObject",
        "url": "https://drvickyjain.com/favicon.svg"
      }
    },
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": `https://drvickyjain.com/blog/${post.slug}`
    },
    "inLanguage": "en-IN",
    "keywords": post.keywords || post.category,
    "articleSection": post.category,
    "wordCount": post.content.reduce((n, s) => n + s.body.split(" ").length, 0),
  };

  return (
    <div className="bg-warm-white min-h-screen">
      <SEO
        title={post.title}
        description={post.metaDescription}
        canonical={`/blog/${post.slug}`}
        ogType="article"
        article={{ publishedTime: post.dateISO || "2026-05-01", modifiedTime: post.dateISO || "2026-05-01", author: "Dr. Vicky Jain", section: post.category }}
        jsonLd={articleSchema}
      />

      {/* Breadcrumb */}
      <nav className="bg-warm-white pt-24 pb-4 px-4 md:px-8">
        <div className="container mx-auto max-w-7xl">
          <ol className="flex flex-wrap items-center gap-2 text-sm text-deep-navy/60">
            <li className="flex items-center gap-2">
              <Link to="/" className="hover:text-gold transition-colors">Home</Link>
            </li>
            <li className="flex items-center gap-2">
              <ChevronRight className="w-3 h-3 text-gold" />
              <Link to="/blog" className="hover:text-gold transition-colors">Blog</Link>
            </li>
            <li className="flex items-center gap-2">
              <ChevronRight className="w-3 h-3 text-gold" />
              <span className="text-deep-navy/70 line-clamp-1">{post.title}</span>
            </li>
          </ol>
        </div>
      </nav>

      {/* Hero */}
      <section className="bg-warm-white pt-8 pb-12 px-4 md:px-8">
        <div className="container mx-auto max-w-3xl">
          <p className="text-xs font-medium tracking-widest text-gold uppercase mb-4">
            {post.category}
          </p>
          <h1 className="font-serif text-3xl sm:text-4xl md:text-5xl text-premium-purple leading-[1.15] mb-6">
            {post.title}
          </h1>
          <div className="flex items-center gap-4 text-sm text-deep-navy/60 mb-2">
            <span>{post.date}</span>
            <span>&middot;</span>
            <span>{post.readTime}</span>
          </div>
          <p className="text-sm text-deep-navy/60">By Dr. Vicky Jain, M.Ch. Plastic & Reconstructive Surgery</p>
        </div>
      </section>

      {/* Article Body */}
      <section className="bg-warm-white pb-16 px-4 md:px-8">
        <div className="container mx-auto max-w-3xl">
          <div className="border-t border-deep-navy/10 pt-12 space-y-8">
            {post.content.map((section, index) => (
              <div key={index}>
                {section.heading && (
                  <h2 className="font-serif text-2xl text-premium-purple mb-4">
                    {section.heading}
                  </h2>
                )}
                <p className="text-deep-navy/80 leading-relaxed text-lg">
                  {section.body}
                </p>
              </div>
            ))}
          </div>

          {/* Author footer */}
          <div className="mt-16 pt-8 border-t border-deep-navy/10">
            <p className="text-sm text-deep-navy/60 leading-relaxed">
              <span className="font-medium text-deep-navy">Dr. Vicky Jain</span> is a board-certified plastic and reconstructive surgeon based with over 17 years of clinical experience.{" "}
              <Link to="/about" className="text-gold hover:text-deep-navy transition-colors">
                Learn more about Dr. Jain →
              </Link>
            </p>
          </div>
        </div>
      </section>

      {/* Related Articles */}
      {related.length > 0 && (
        <section className="bg-warm-white py-16 px-4 md:px-8 border-t border-deep-navy/10">
          <div className="container mx-auto max-w-7xl">
            <h2 className="font-serif text-2xl text-premium-purple mb-8">More Articles</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {related.map((rel) => (
                <div key={rel.slug} className="bg-white p-8 border border-deep-navy/10">
                  <p className="text-xs font-medium tracking-widest text-gold uppercase mb-3">
                    {rel.category}
                  </p>
                  <h3 className="font-serif text-xl text-premium-purple mb-3 leading-snug">
                    {rel.title}
                  </h3>
                  <p className="text-deep-navy/70 leading-relaxed mb-6 text-sm">
                    {rel.excerpt}
                  </p>
                  <Link
                    to={`/blog/${rel.slug}`}
                    className="text-gold font-medium hover:text-deep-navy transition-colors text-sm"
                  >
                    Read Article →
                  </Link>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="bg-premium-purple py-16 px-4 md:px-8">
        <div className="container mx-auto max-w-7xl">
          <div className="flex flex-col md:flex-row items-center justify-between gap-8">
            <div>
              <h2 className="font-serif text-3xl text-warm-white mb-3">
                Book a Consultation
              </h2>
              <p className="text-warm-white/70">
                Every treatment starts with a detailed, one-on-one consultation.
              </p>
            </div>
            <Button asChild className="btn-gold border-none rounded-none px-8 py-6 shrink-0">
              <a href={WHATSAPP_LINK} target="_blank" rel="noopener noreferrer">
                Book Consultation
              </a>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default BlogArticlePage;
